import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
} from "@material-ui/core";
import Select from "react-select";
import { getFetch, postData } from "../../utils/fetchUtils";

type EditDVResponsibleModalProps = {
  open: boolean;
  onClose: () => void;
  outletName: string;
  outletId: number;
  currentResponsible: string;
  onSave: (outletId: number, newResponsibleUsername: string) => void;
  onSuccess: (isError?: boolean) => void;
};

const EditDVResponsibleModal = ({
  open,
  onClose,
  outletName,
  outletId,
  currentResponsible,
  onSave,
  onSuccess,
}: EditDVResponsibleModalProps) => {
  const [responsibles, setResponsibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      getFetch("/dv-responsible", {})
        .then((data) => {
          const options = data.map((item) => ({
            value: item.id,
            label: item.username,
          }));
          setResponsibles(options);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [open]);

  const handleSave = async () => {
    if (!selectedResponsible) return;

    setSaving(true);
    try {
      await postData("/dv-responsible", {
        data: {
          outlet_id: outletId,
          new_responsible_id: selectedResponsible.value,
        },
        api: null,
        method: "PUT",
      });
      onSave(outletId, selectedResponsible.label);
      onSuccess();
      onClose();
    } catch (error) {
      onSuccess(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedResponsible(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit DV Responsible for {outletName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box mb={1}>
              <Typography variant="body2">
                Current responsible: <strong>{currentResponsible}</strong>
              </Typography>
            </Box>
            <Box width="100%">
              <Select
                name="dvResponsible"
                isMulti={false}
                isClearable={false}
                options={responsibles}
                placeholder="Select new responsible"
                onChange={setSelectedResponsible}
                value={selectedResponsible}
                closeMenuOnSelect={true}
                className="select-dropdown-std"
                isSearchable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={saving}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  container: (base) => ({ ...base, width: "100%" }),
                }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={!selectedResponsible || saving}>
          Edit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDVResponsibleModal;
